import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { translate } from 'react-i18next'
import { connect } from 'react-redux'
import _ from 'lodash'

import * as localUsersActions from 'action/localUsers'
import ContentHeader from 'component/utils/ContentHeader'
import Title from 'component/localUsers/Title'
import Grid from 'component/utils/Grid'
import Col from 'component/utils/Col'
import Breadcrumb from 'component/Breadcrumb'
import EditForm from 'component/localUsers/EditForm'
import Table from 'component/localUsers/Table'
import Notification from 'component/utils/Notification'
import Pagination from 'component/pagination/Pagination'
import DeleteModal from 'component/localUsers/DeleteModal'
import UpdateModal from 'component/localUsers/UpdateModal'
import { ToastContainer } from 'react-toastify'
import { renderToastSuccessMsg, renderToastWarn } from 'utils/ToastUtils'

import 'styles/overwrittenAndNew/localUsers.css'

const DEFAULT_PAGE = 1
const DEFAULT_SIZE = 10

class LocalUsersContainer extends Component {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    count: PropTypes.number,
    current: PropTypes.number,
    editError: PropTypes.object,
    fetch: PropTypes.func.isRequired,
    listError: PropTypes.object,
    logout: PropTypes.func.isRequired,
    menu: PropTypes.object.isRequired,
    pages: PropTypes.number,
    records: PropTypes.array.isRequired,
    remove: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
  }

  constructor() {
    super()

    this.state = {
      renderDeleteModal: false,
      renderUpdateModal: false,
      resetFormTrigger: false,
      scrollTop: 0,
      sideFilterElement: null,
      sideFilterVisible: false,
      sideFilterWidth: 0,
      topMargin: 0,
    }
  }

  async componentDidMount() {
    const {
      auth: { user },
      fetch,
    } = this.props

    const filters = {
      page: DEFAULT_PAGE,
      size: DEFAULT_SIZE,
    }

    await fetch(user ? user.dsToken : null, filters)

    this.handleResize()

    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleScroll)
  }

  handleScroll = () => {
    const { scrollTop } = document.documentElement

    if (scrollTop < 70) {
      this.setState({ scrollTop })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll)
  }

  handleResize = () => {
    const { sideFilterElement, sideFilterVisible, sideFilterWidth, topMargin } =
      this.state

    if (!_.isEmpty(this.refs)) {
      const { header } = this.refs
      const { filter } = this.refs
      const { table } = this.refs

      if (window.innerWidth > 768) {
        const topHeight = header.clientHeight + filter.clientHeight

        filter.style.top = `${header.clientHeight}px`

        if (table) {
          table.style.paddingTop = 0
        }

        const newSideFilterWidth =
          sideFilterVisible && sideFilterElement
            ? sideFilterElement.clientWidth
            : sideFilterWidth

        if (topMargin !== topHeight || sideFilterWidth !== newSideFilterWidth) {
          this.setState({
            topMargin: topHeight,
            sideFilterWidth: newSideFilterWidth,
          })
        }
      } else {
        if (table) {
          table.style.paddingTop = `0px`
        }
      }
    }
  }

  handleSave = (params) => {
    const {
      auth: {
        user: { dsToken },
      },
      fetch,
      save,
      translate,
    } = this.props

    const successCallback = async () => {
      this.setState(({ resetFormTrigger }) => ({
        resetFormTrigger: !resetFormTrigger,
      }))

      const filters = {
        page: DEFAULT_PAGE,
        size: DEFAULT_SIZE,
      }

      await fetch(dsToken, filters)

      renderToastSuccessMsg(translate('localUsers.editForm.save.success'))
    }

    const errorCallback = (dsError) => {
      renderToastWarn(translate(dsError || 'localUsers.editForm.save.error'))
    }

    save(dsToken, params, successCallback, errorCallback)
  }

  renderBreadcrumb = () => {
    const { translate } = this.props

    const steps = [
      { link: '/', title: translate('mobile.home') },
      { title: translate('mobile.register') },
      { title: translate('menu.config.localUsers'), strong: true },
    ]

    return <Breadcrumb steps={steps} />
  }

  handlePagination = (page) => {
    const {
      auth: {
        user: { dsToken },
      },
      fetch,
    } = this.props

    const filters = {
      page,
      size: DEFAULT_SIZE,
    }

    fetch(dsToken, filters)
  }

  renderUpdateModal = () => {
    const {
      auth: { user },
      translate,
    } = this.props

    const { record, renderUpdateModal } = this.state

    if (!renderUpdateModal) return

    return (
      <UpdateModal
        closeModal={() => this.setState({ renderUpdateModal: false })}
        handleUpdate={this.handleUpdate}
        initialValues={record}
        token={user ? user.dsToken : null}
        translate={translate}
      />
    )
  }

  handleUpdate = (record) => {
    const {
      auth: {
        user: { dsToken },
      },
      fetch,
      save,
      translate,
    } = this.props

    const successCallback = async () => {
      this.setState({ renderUpdateModal: false })

      const filters = {
        page: DEFAULT_PAGE,
        size: DEFAULT_SIZE,
      }

      await fetch(dsToken, filters)

      renderToastSuccessMsg(translate('localUsers.modal.update.success'))
    }

    const errorCallback = (dsError) => {
      renderToastWarn(translate(dsError || 'localUsers.modal.update.error'))
    }

    save(dsToken, record, successCallback, errorCallback)
  }

  renderDeleteModal = () => {
    const { translate } = this.props

    const { renderDeleteModal } = this.state

    if (!renderDeleteModal) return

    return (
      <DeleteModal
        closeModal={() => this.setState({ renderDeleteModal: false })}
        handleDelete={this.handleDelete}
        translate={translate}
      />
    )
  }

  handleDelete = () => {
    const {
      auth: {
        user: { dsToken },
      },
      fetch,
      translate,
      remove,
    } = this.props

    const { idLocalUsers } = this.state

    const successCallback = async () => {
      this.setState({ renderDeleteModal: false })

      const filters = {
        page: DEFAULT_PAGE,
        size: DEFAULT_SIZE,
      }

      await fetch(dsToken, filters)

      renderToastSuccessMsg(translate('localUsers.modal.delete.success'))
    }

    const errorCallback = () => {
      renderToastWarn(translate('localUsers.modal.delete.error'))
    }

    remove(dsToken, { idLocalUsers }, successCallback, errorCallback)
  }

  handleFilterCollapseChange = (sideFilterVisible) => {
    this.setState({ sideFilterVisible })
  }

  render() {
    const {
      auth: { user },
      count,
      current,
      translate,
      pages,
      records,
    } = this.props

    const {
      resetFormTrigger,
      scrollTop,
      sideFilterElement,
      sideFilterVisible,
      topMargin,
    } = this.state

    return (
      <div className='stock-adjustment'>
        <div ref='header' className='stock-adjustment-header'>
          <ContentHeader>{translate('menu.config.localUsers')}</ContentHeader>

          {this.renderBreadcrumb()}
        </div>

        <div ref='filter' className='stock-adjustment-filter'>
          <Grid fluid>
            <Title
              handleSideFilterMenuCollapseChange={
                this.handleFilterCollapseChange
              }
              sideFilterVisible={sideFilterVisible}
              translate={translate}
            />
          </Grid>
        </div>

        <div className='local-users-table stock-adjustment-table'>
          <Grid fluid className='no-padding'>
            <div className='outer-table'>
              <div style={{ display: 'flex' }}>
                <EditForm
                  handleSubmit={this.handleSave}
                  resetFormTrigger={resetFormTrigger}
                  scrollTop={scrollTop}
                  sideFilterVisible={sideFilterVisible}
                  translate={translate}
                  token={user ? user.dsToken : null}
                />

                {records && records.length ? (
                  <div
                    style={{
                      paddingLeft: `${sideFilterVisible ? 1 : 2}rem`,
                      paddingRight: '2rem',
                      marginTop: '1rem',
                    }}
                  >
                    <Table
                      handleDelete={(idLocalUsers) =>
                        this.setState({ renderDeleteModal: true, idLocalUsers })
                      }
                      handleResize={this.handleResize}
                      handleUpdate={(record) =>
                        this.setState({ renderUpdateModal: true, record })
                      }
                      records={records}
                      sideFilterElement={sideFilterElement}
                      sideFilterVisible={sideFilterVisible}
                      topMargin={topMargin}
                      translate={translate}
                    />
                  </div>
                ) : (
                  <Col
                    xs={12}
                    style={{
                      marginLeft: `${sideFilterVisible ? 0.5 : 1}rem`,
                      marginRight: '1rem',
                      marginTop: '1rem',
                    }}
                  >
                    <Notification
                      className='notification-warning'
                      message={translate('no.data.found')}
                    />
                  </Col>
                )}
              </div>

              {!!pages && pages > 1 && (
                <Col className='align-center rupture-irc-pagination'>
                  <Pagination
                    count={count}
                    current={current}
                    getPage={this.handlePagination}
                    pages={pages}
                  />
                </Col>
              )}
            </div>
          </Grid>
        </div>

        {this.renderUpdateModal()}
        {this.renderDeleteModal()}

        <ToastContainer newestOnTop />
      </div>
    )
  }
}

/* istanbul ignore next */
const mapStateToProps = ({ localUsersReducer, paginateReducer }) => ({
  listError: localUsersReducer.list.error,
  records: localUsersReducer.list.records,
  deleted: localUsersReducer.edit.deleted,
  editError: localUsersReducer.edit.error,
  updated: localUsersReducer.edit.updated,
  count: paginateReducer.count,
  current: paginateReducer.current,
  pages: paginateReducer.pages,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(localUsersActions, dispatch)

const LocalUsersContainerTranslated = translate(null, {
  wait: true,
  translateFuncName: 'translate',
})(LocalUsersContainer)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LocalUsersContainerTranslated)

export { LocalUsersContainer }
